/**
 * ConflictTargetPicker — multi-select popover over the INSERT target table's
 * columns that rewrites the ON CONFLICT `(…)` conflict-target group via
 * `modify-insert-conflict-target` (§2.4 S6b). Mirrors the add-column chooser's
 * gating: schema-consuming picker ⇒ editReady (snapshot) required, disabled
 * trigger carries the not-ready tooltip (decision 7). Apply requires ≥1
 * column (a target-less rewrite would emit `ON CONFLICT () …`).
 *
 * Extraction of `OnConflictCardEditable` (file-size budget) — mounted only
 * from the editable ON CONFLICT card.
 */

import { useState } from 'react';
import { Check } from 'lucide-react';

import { Button } from '../../../../../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../../components/ui/popover';
import type { OnConflictSpec, QueryStructure } from '../../../../sql-resolver/types/queryStructure';
import type { VqeEditContext } from '../../edit/editContext';
import { toCommitCtx } from '../../edit/editContext';
import { modifyInsertConflictTarget } from '../../edit/edits/insertConflictClause';
import { VqeTooltip } from '../common/VqeTooltip';
import { vqeCopy } from '../../ux/vqeCopy';
import { recordEditFailure, safeExtractStructure } from './insertColumnMapHelpers';
import { computeConflictTargetCandidates } from './insertTailHelpers';

export interface ConflictTargetPickerProps {
  readonly editContext: VqeEditContext;
  readonly structure: QueryStructure;
  readonly onConflict: OnConflictSpec;
  /** Testid root — `vqe-insert-on-conflict-target` in the plain INSERT mount. */
  readonly testId: string;
}

export function ConflictTargetPicker({
  editContext,
  structure,
  onConflict,
  testId,
}: ConflictTargetPickerProps) {
  const { items, nameByKey, currentKeys } = computeConflictTargetCandidates(
    editContext,
    structure,
    onConflict,
  );
  const enabled = items.length > 0;
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<ReadonlySet<string>>(currentKeys);

  const changeOpen = (next: boolean): void => {
    // Re-seed the selection from the CURRENT target every time the picker
    // opens, so a previous cancel doesn't leak stale checks.
    if (next) setSelected(new Set(currentKeys));
    setOpen(next);
  };

  const toggle = (key: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const apply = (): void => {
    // Snapshot order keeps the emitted list deterministic regardless of
    // click order.
    const names = items
      .filter((item) => selected.has(item.key))
      .map((item) => nameByKey.get(item.key) ?? item.label);
    if (names.length === 0) return;
    modifyInsertConflictTarget(names.join(', '), toCommitCtx(editContext), {
      structure: safeExtractStructure(editContext),
    }).catch(recordEditFailure);
    setOpen(false);
  };

  if (!enabled) {
    return (
      <VqeTooltip content={vqeCopy.disabled.noSnapshot}>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-6 px-2 text-[11px]"
          disabled
          aria-disabled
          data-testid={testId}
        >
          {vqeCopy.actions.editConflictTarget}
        </Button>
      </VqeTooltip>
    );
  }

  return (
    <Popover open={open} onOpenChange={changeOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-6 px-2 text-[11px]"
          data-testid={testId}
          aria-label="Edit the conflict target columns"
        >
          {vqeCopy.actions.editConflictTarget}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-64 space-y-2 text-xs"
        data-testid={`${testId}-content`}
        // Same focus-outside guard as the sibling pickers — a late
        // schema-refresh re-render must not tear the open list down.
        onFocusOutside={(e) => e.preventDefault()}
      >
        <ul className="m-0 flex list-none flex-col gap-0.5 p-0" data-testid={`${testId}-items`}>
          {items.map((item) => (
            <li key={item.key}>
              <button
                type="button"
                className="flex w-full items-center gap-2 rounded px-2 py-1 text-left hover:bg-muted"
                data-testid={`${testId}-option-${item.key}`}
                aria-pressed={selected.has(item.key)}
                onClick={() => toggle(item.key)}
              >
                {/* Presentational check indicator — a real <Checkbox> is a
                    <button> and cannot nest inside this row button. */}
                <span
                  aria-hidden
                  className={
                    selected.has(item.key)
                      ? 'flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-primary bg-primary text-primary-foreground'
                      : 'flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-primary'
                  }
                >
                  {selected.has(item.key) ? <Check className="h-3 w-3" /> : null}
                </span>
                <span className="font-mono text-xs">{item.label}</span>
              </button>
            </li>
          ))}
        </ul>
        <div className="flex justify-end gap-2 border-t border-border pt-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={() => changeOpen(false)}
            data-testid={`${testId}-cancel`}
          >
            {vqeCopy.actions.cancelConflictTarget}
          </Button>
          <VqeTooltip
            content={selected.size === 0 ? vqeCopy.disabled.conflictTargetNeedsColumn : undefined}
          >
            <Button
              type="button"
              size="sm"
              className="h-7 text-xs"
              disabled={selected.size === 0}
              aria-disabled={selected.size === 0}
              onClick={apply}
              data-testid={`${testId}-apply`}
            >
              {vqeCopy.actions.applyConflictTarget}
            </Button>
          </VqeTooltip>
        </div>
      </PopoverContent>
    </Popover>
  );
}
